import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query';
import { PageDataWithCount } from '../../useInfiniteDataQuery.types';
import { CacheData } from './useMutateInfiniteDataQuery.types';
import { useMutateInfiniteDataQueryUtils } from './useMutateInfiniteDataQuery.utils';

export const useMutateInfiniteDataQuery = <PageData>({
  queryKey,
}: UseMutateInfiniteDataQueryParams) => {
  const queryClient = useQueryClient();

  const mutateByIndex = (index: number, newData: PageData) => {
    queryClient.setQueryData(queryKey, (oldData?: InfiniteData<PageDataWithCount>) => {
      if (!oldData) {
        return oldData;
      }

      const indexes = useMutateInfiniteDataQueryUtils.findIndexesByGlobalIndex(oldData, index);
      if (!indexes) {
        return oldData;
      }

      const { pageIndex, itemIndexInPage } = indexes;

      return useMutateInfiniteDataQueryUtils.mapPagesAt(oldData, pageIndex, (page) => {
        const updatedPageData = page.data.map((item, currentItemIndex) =>
          currentItemIndex === itemIndexInPage ? newData : item,
        );

        return {
          ...page,
          data: updatedPageData,
        };
      });
    });
  };

  const removeByIndex = (index: number) => {
    queryClient.setQueryData(queryKey, (oldData?: InfiniteData<PageDataWithCount>) => {
      if (!oldData) {
        return oldData;
      }

      const indexes = useMutateInfiniteDataQueryUtils.findIndexesByGlobalIndex(oldData, index);
      if (!indexes) {
        return oldData;
      }

      const { pageIndex, itemIndexInPage } = indexes;

      const updatedPages = useMutateInfiniteDataQueryUtils.mapPagesAt(
        oldData,
        pageIndex,
        (page) => {
          const updatedPageData = page.data.filter(
            (_, currentItemIndex) => currentItemIndex !== itemIndexInPage,
          );

          return {
            ...page,
            data: updatedPageData,
          };
        },
      );

      const countUpdated = useMutateInfiniteDataQueryUtils.mapPagesAt(
        updatedPages,
        0,
        (firstPage) => {
          if (!firstPage.countData) {
            return firstPage;
          }

          return {
            ...firstPage,
            countData: {
              ...firstPage.countData,
              total: Math.max(0, firstPage.countData.total - 1),
            },
          };
        },
      );

      return countUpdated;
    });
  };

  const updateItems = (updater: (item: PageData) => PageData) => {
    queryClient.setQueryData(queryKey, (oldData?: CacheData<PageData>) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          data: page.data.map(updater),
        })),
      };
    });
  };

  const replaceAllItems = (newItems: PageData[]) => {
    queryClient.setQueryData(queryKey, (oldData?: CacheData<PageData>) => {
      if (!oldData) {
        return oldData;
      }
      let offset = 0;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => {
          const pageSize = page.data.length;
          const slice = newItems.slice(offset, offset + pageSize);
          offset += pageSize;
          return { ...page, data: slice };
        }),
      };
    });
  };

  const getSnapshot = () => queryClient.getQueryData<CacheData<PageData>>(queryKey);

  const cancelQueries = () => queryClient.cancelQueries({ queryKey });

  const restoreSnapshot = (snapshot: CacheData<PageData> | undefined) => {
    if (snapshot !== undefined) {
      queryClient.setQueryData(queryKey, snapshot);
    }
  };

  return {
    mutateByIndex,
    removeByIndex,
    updateItems,
    replaceAllItems,
    getSnapshot,
    cancelQueries,
    restoreSnapshot,
  };
};

interface UseMutateInfiniteDataQueryParams {
  queryKey: QueryKey;
}
